/**
 * useMemoCreate — shared hook for the note-creation pipeline.
 *
 * Encapsulates everything both NoteCreateModal and MemoComposePanel need:
 *   - Auth / org / association-type setup
 *   - Linked-associations state + handlers
 *   - The event-building submit handler
 *   - The "Linked records" slot JSX (pre-linked entity badges + link picker rows)
 *
 * Both surfaces call this hook and wire the returned values into their
 * own MemoForm instance. The submit pipeline, association logic, and
 * linked-records UI are defined once here — no mirroring.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks';
import type { MemoComposeConfig } from '@/contexts/MemoComposeContext';
import { useOrg } from '@/contexts/OrgContext';
import { useOrgMembers } from '@/hooks/useOrgMembers';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import { useAssociationMatchers } from '@/lib/crm/hooks/useAssociationMatchers';
import { crmQueryKeys } from '@/lib/crm/query/queries';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import { getEntityKinds } from '@/lib/crm/utils/associationMatching';
import { logger } from '@/lib/logger';

import {
  filterLinkAssociationTypes,
  HIDDEN_ASSOCIATION_TYPES_BY_ENTITY_TYPE,
  toLinkPickerItems,
} from './linkPickerFilters';
import { LinkRecordsEditor } from './LinkRecordsEditor';
import { extractMentionedUserIds } from './mentionUtils';
import {
  dueProperties,
  getMemoAssociationNames,
  type MemoEntityType,
  OWNER_ASSOCIATION_TYPE_NAME,
  validateMentionedUserIds,
} from './notes.utils';
import { useLinkRecordRows } from './useLinkRecordRows';

interface UseMemoCreateOptions {
  orgId: number;
  /** Configuration for the note (pre-linked entities, stage, etc.). */
  config?: MemoComposeConfig;
  /** Called after a successful create. */
  onSuccess?: (entityId: string) => void;
  /** Called after a successful create or when the user cancels. */
  onClose: () => void;
  /** Label for the log component (e.g. 'NoteCreateModal', 'MemoComposePanel'). */
  logComponent?: string;
}

export interface MemoCreateResult {
  /** Wire this as MemoForm's `onSubmit`. */
  handleSubmit: (
    title: string,
    content: string,
    date?: string,
    ownerEntityId?: string,
    stageAssociation?: string | null,
    privacy?: 'private' | 'shared'
  ) => void;
  /** Wire this as MemoForm's `onCancel`. */
  handleClose: () => void;
  /** Pass to MemoForm's `isSubmitting`. */
  isSubmitting: boolean;
  /** Pass to MemoForm's `error`. */
  submitError: Error | null;
  /** Pass to MemoForm's `currentUserEntityId`. */
  userEntityId: string | undefined;
  /** Pass to MemoForm's `slotAfterContent`. */
  linkedRecordsSlot: React.ReactNode;
}

/**
 * Find the `recordType`-sourced association that links to the same target
 * as `noteSourced` (e.g. `ahoy_note_to_contact` → the task's contact edge).
 * The link pickers are built from the note family (the composition's type is
 * only known at submit), so a staged composition on a migrated org remaps its
 * chosen links onto the task family here. Returns null when the task family
 * has no counterpart for that target.
 *
 * Both sides can be KIND-based. A task links a contact through the case-kind
 * `ahoy_deal_to_contact` (source kind `case` — the same edge the notes → tasks
 * conversion writes), so the source side goes through the kind-aware
 * `matchesSource`. And the note edge itself may name only a target KIND
 * (`ahoy_note_to_contact` targets kind `person`), so the target side is
 * matched against the linked entity's concrete type when the caller knows
 * it, else against the note edge's target type or kind. A type-named
 * counterpart wins over a kind-based one when both exist.
 */
function findCounterpartAssociation(
  noteSourced: AssociationType,
  recordType: MemoEntityType,
  associationTypes: AssociationType[],
  matchers: {
    matchesSource: (at: AssociationType, sourceTypeName: string) => boolean;
    matchesTarget: (at: AssociationType, targetTypeName: string) => boolean;
    entityKindMap: ReadonlyMap<string, readonly string[]>;
  },
  targetEntityTypeName?: string
): AssociationType | null {
  if (noteSourced.source_entity_type_name === recordType) {
    return noteSourced;
  }
  const { matchesSource, matchesTarget, entityKindMap } = matchers;
  const targetTypeName =
    targetEntityTypeName || noteSourced.target_entity_type_name;
  const targetKindName = noteSourced.target_entity_kind_name;
  const targetCompatible = (at: AssociationType): boolean => {
    if (targetTypeName) {
      return matchesTarget(at, targetTypeName);
    }
    if (!targetKindName) {
      return false;
    }
    if (at.target_entity_kind_name) {
      return at.target_entity_kind_name === targetKindName;
    }
    return getEntityKinds(at.target_entity_type_name, entityKindMap).includes(
      targetKindName
    );
  };
  const candidates = associationTypes.filter(
    at =>
      at.value_write_access !== 'internal' &&
      matchesSource(at, recordType) &&
      targetCompatible(at)
  );
  return (
    candidates.find(at => at.source_entity_type_name === recordType) ??
    candidates[0] ??
    null
  );
}

export function useMemoCreate({
  orgId,
  config,
  onSuccess,
  onClose,
  logComponent = 'useMemoCreate',
}: UseMemoCreateOptions): MemoCreateResult {
  const { user } = useAuthInfo();
  const { currentOrg } = useOrg();
  const queryClient = useQueryClient();

  const { members: orgUsers } = useOrgMembers(orgId);
  const currentMember = orgUsers.find(u => u.userId === user?.id);
  const userEntityId = currentMember?.entityId;

  // Association types + kind-aware matchers for resolving linked entities
  const { associationTypes, matchesSource, matchesTarget, entityKindMap } =
    useAssociationMatchers(orgId);

  const linkableAssocTypes = useMemo(
    () =>
      filterLinkAssociationTypes(
        associationTypes.filter(
          at =>
            at.source_entity_type_name === 'ahoy_note' &&
            // the composer picker is note-family (source==='ahoy_note'); it
            // remaps to the task family at submit, so it hides the
            // note-family system edges.
            !HIDDEN_ASSOCIATION_TYPES_BY_ENTITY_TYPE['ahoy_note'].has(
              at.association_type_name
            )
        )
      ),
    [associationTypes]
  );

  const ownerAssociationTypeName = useMemo(() => {
    const assocType = associationTypes.find(
      at => at.association_type_name === OWNER_ASSOCIATION_TYPE_NAME
    );
    return assocType?.association_type_name || null;
  }, [associationTypes]);

  // Resolve association type names for pre-linked entities.
  // Uses kind-aware matching so contacts (entity_kind=person) resolve correctly.
  const resolvedLinkedEntities = useMemo(() => {
    if (!config?.linkedEntities?.length) {
      return [];
    }
    return config.linkedEntities
      .map(le => {
        const assocType = associationTypes.find(
          at =>
            at.source_entity_type_name === 'ahoy_note' &&
            at.value_write_access !== 'internal' &&
            matchesTarget(at, le.entityTypeName)
        );
        if (!assocType) {
          logger.warn(
            `${logComponent}: could not resolve association type for pre-linked entity`,
            { org_id: orgId }
          );
        }
        return assocType
          ? {
              entityId: le.entityId,
              entityTypeName: le.entityTypeName,
              associationType: assocType,
            }
          : null;
      })
      .filter(Boolean) as Array<{
      entityId: string;
      entityTypeName: string;
      associationType: AssociationType;
    }>;
  }, [
    config?.linkedEntities,
    associationTypes,
    matchesTarget,
    logComponent,
    orgId,
  ]);

  // ── Linked associations state ──────────────────────────────────────────
  // Deferred-commit: rows accumulate here and are turned into association
  // events at note-create time (handleSubmit). No `onRowComplete`.
  const {
    rows: linkedAssociations,
    addRow: handleAddLink,
    removeRow: handleRemoveLink,
    setAssociationType: handleAssociationTypeChange,
    setTarget: handleTargetEntityChange,
    reset: resetLinkedAssociations,
  } = useLinkRecordRows(linkableAssocTypes);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<Error | null>(null);

  // ── Close (resets state) ───────────────────────────────────────────────

  const handleClose = useCallback(() => {
    resetLinkedAssociations();
    setSubmitError(null);
    setIsSubmitting(false);
    onClose();
  }, [onClose, resetLinkedAssociations]);

  // ── Submit handler (event-building pipeline) ───────────────────────────

  const handleSubmit = useCallback(
    async (
      title: string,
      content: string,
      date?: string,
      ownerEntityIdArg?: string,
      stageAssociationArg?: string | null,
      privacyArg?: 'private' | 'shared'
    ) => {
      const userId = user?.id ? String(user.id) : undefined;
      if (!userEntityId || !userId) {
        setSubmitError(
          new Error('Cannot create note — your org membership is not ready')
        );
        return;
      }
      if (!ownerAssociationTypeName) {
        setSubmitError(
          new Error('Cannot create note — owner association schema missing')
        );
        return;
      }

      const ownerEntityId = ownerEntityIdArg || userEntityId;
      const orgEntityId = currentOrg?.entityId ?? null;
      const requestedPrivacy = privacyArg ?? 'shared';
      const isShared = requestedPrivacy === 'shared' && !!orgEntityId;
      const sharedRequestedButFellBack =
        requestedPrivacy === 'shared' && !orgEntityId;

      if (sharedRequestedButFellBack) {
        logger.warn(
          `${logComponent}: fell back to private because org has no entity_id`,
          { org_id: orgId }
        );
      }

      // The record's type is the type the caller REQUESTED (config.entityType),
      // independent of the stage: a task stays an ahoy_task even with no stage
      // or a cleared one (epic #13640). Defaults to ahoy_note. Every
      // association below is written under that type's own family.
      const recordType: MemoEntityType = config?.entityType ?? 'ahoy_note';
      const names = getMemoAssociationNames(recordType);
      // The pickers were built from the note family (the type wasn't known
      // yet); remap each chosen link onto the record type's counterpart and
      // drop — loudly — any link the task family can't express.
      const remapLink = (
        at: AssociationType,
        targetEntityTypeName?: string
      ): AssociationType | null => {
        const counterpart = findCounterpartAssociation(
          at,
          recordType,
          associationTypes,
          { matchesSource, matchesTarget, entityKindMap },
          targetEntityTypeName
        );
        if (!counterpart) {
          logger.warn(
            `${logComponent}: no ${recordType} counterpart for link association; link dropped`,
            { org_id: orgId, association_type_name: at.association_type_name }
          );
        }
        return counterpart;
      };

      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const noteEntityId = crypto.randomUUID();
        await submitEventsOptimistic({
          orgId,
          userId,
          entityTypeName: recordType,
          primaryEntityId: noteEntityId,
          build: builder => {
            // Core note properties. `date` carries a YYYY-MM-DD date or a full
            // ISO datetime (when a time is set) — store it on the right property.
            const due = dueProperties(date);
            builder.objectCreated(noteEntityId, recordType, {
              ahoy_title: title,
              ahoy_content: content,
              ...(due.ahoy_datetime
                ? { ahoy_datetime: due.ahoy_datetime }
                : {}),
              ...(due.ahoy_date ? { ahoy_date: due.ahoy_date } : {}),
            });

            // Owner
            builder.associationAdded(
              noteEntityId,
              recordType,
              names.owner,
              ownerEntityId
            );

            // Creator
            builder.associationAdded(
              noteEntityId,
              recordType,
              names.creator,
              userEntityId
            );

            // Visibility
            if (isShared && orgEntityId) {
              builder.associationAdded(
                noteEntityId,
                recordType,
                names.visibleOrg,
                orgEntityId
              );
            } else {
              builder.associationAdded(
                noteEntityId,
                recordType,
                names.visibleUser,
                userEntityId
              );
            }

            // Stage (opt-in). names.stage is only non-null when recordType
            // resolved to 'ahoy_task' above (a stageAssociationArg forces
            // that), so this never fires for the note family.
            if (stageAssociationArg && names.stage) {
              builder.associationAdded(
                noteEntityId,
                recordType,
                names.stage,
                stageAssociationArg
              );
            }

            // Pre-linked entities (meetings, contacts, deals, etc.)
            for (const le of resolvedLinkedEntities) {
              const at = remapLink(le.associationType, le.entityTypeName);
              if (!at) {
                continue;
              }
              builder.associationAdded(
                noteEntityId,
                recordType,
                at.association_type_name,
                le.entityId
              );
            }

            // User-added links
            for (const link of linkedAssociations) {
              if (!link.associationType || link.targetEntityIds.length === 0) {
                continue;
              }
              const at = remapLink(link.associationType);
              if (!at) {
                continue;
              }
              for (const targetEntityId of link.targetEntityIds) {
                builder.associationAdded(
                  noteEntityId,
                  recordType,
                  at.association_type_name,
                  targetEntityId
                );
              }
            }

            // Mention associations from content body
            const validUserEntityIds = new Set(orgUsers.map(u => u.entityId));
            const mentionedIds = extractMentionedUserIds(content);
            const validMentions = validateMentionedUserIds(
              mentionedIds,
              validUserEntityIds
            );
            for (const mentionedUserId of validMentions) {
              builder.associationAdded(
                noteEntityId,
                recordType,
                names.mentionedUser,
                mentionedUserId
              );
            }
          },
          errorToast: false,
        });

        // Fire-and-forget: cache refresh is best-effort; a failed refetch must not
        // surface as "note creation failed" when the note was actually persisted.
        queryClient
          .invalidateQueries({
            queryKey: crmQueryKeys.objects(orgId, recordType),
          })
          .catch((err: unknown) => {
            logger.warn(
              `${logComponent}: cache invalidation failed after note create`,
              {
                org_id: orgId,
                error_type:
                  err instanceof Error ? err.constructor.name : 'unknown',
              }
            );
          });

        if (sharedRequestedButFellBack) {
          toast.error(
            "Saved as private – your org isn't fully set up for shared notes yet. Contact support if this is unexpected."
          );
        }

        onSuccess?.(noteEntityId);
        handleClose();
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error(`${logComponent}: failed to create note`, error, {
          org_id: orgId,
        });
        setSubmitError(error);
        setIsSubmitting(false);
      }
    },
    [
      config?.entityType,
      orgId,
      user?.id,
      userEntityId,
      ownerAssociationTypeName,
      currentOrg?.entityId,
      orgUsers,
      linkedAssociations,
      resolvedLinkedEntities,
      logComponent,
      onSuccess,
      handleClose,
      queryClient,
      associationTypes,
      matchesSource,
      matchesTarget,
      entityKindMap,
    ]
  );

  // ── Linked records slot (shared JSX) ───────────────────────────────────

  const associationTypeSelectItems = useMemo(
    () => toLinkPickerItems(linkableAssocTypes),
    [linkableAssocTypes]
  );

  // Pre-linked entity badges (from config) shown above the editable rows.
  const prelinkedBadges = useMemo(() => {
    if (resolvedLinkedEntities.length === 0) {
      return null;
    }
    return (
      <div className='flex items-center gap-1 flex-wrap'>
        {resolvedLinkedEntities.map(le => (
          <EntityBadgeById
            key={le.entityId}
            entityId={le.entityId}
            orgId={orgId}
            variant='xs'
          />
        ))}
      </div>
    );
  }, [resolvedLinkedEntities, orgId]);

  const linkedRecordsSlot = useMemo(
    () => (
      <LinkRecordsEditor
        orgId={orgId}
        rows={linkedAssociations}
        items={associationTypeSelectItems}
        linkableCount={linkableAssocTypes.length}
        onAddRow={handleAddLink}
        onRemoveRow={handleRemoveLink}
        onAssociationTypeChange={handleAssociationTypeChange}
        onTargetChange={handleTargetEntityChange}
        isSubmitting={isSubmitting}
        header={prelinkedBadges}
      />
    ),
    [
      orgId,
      linkedAssociations,
      associationTypeSelectItems,
      linkableAssocTypes.length,
      handleAddLink,
      handleRemoveLink,
      handleAssociationTypeChange,
      handleTargetEntityChange,
      isSubmitting,
      prelinkedBadges,
    ]
  );

  return {
    handleSubmit,
    handleClose,
    isSubmitting,
    submitError,
    userEntityId,
    linkedRecordsSlot,
  };
}
